import React, { useMemo } from 'react';
import useFormatMessage from 'hooks/useFormatMessage';
import { getDimensionValue } from 'components/DimensionValue';
import { useMatomoGoalNames } from 'hooks/useMatomoGoalNames';
import { useWebsiteCurrency } from 'hooks/useWebsiteCurrency';
import { formatDateRange } from 'shared/constants/date';
import { dayjs } from 'shared/utils/date';
import { getFormatter } from 'shared/utils/formatter';
import { getMetricPrecision } from 'shared/utils/metricPrecision';
import { metricSupportsShareOfTotal } from 'shared/utils/metricChange';
import { fraction } from 'shared/constants/math';
import Chart from './Chart';
import { metricsTooltipFormatter } from './tooltip';
import { getRowId } from './utils';

const BarChartResult = ({ results, config, segments, mainSegment, selectedMetric }) => {
  const $t = useFormatMessage();
  const { goalNames, loading: goalNamesLoading } = useMatomoGoalNames();
  const { currency, ready } = useWebsiteCurrency();
  const displayCurrency = ready ? currency : null;
  const metrics = config?.metrics || [];
  const dimensions = config?.dimensions || [];
  const { verticalBars, hideLabels, shareOfTotal } = config?.settings || {};

  const primaryResult = results?.find(r => r.segmentId === mainSegment.id);

  const dimensionIds = dimensions.map(d => d.name);
  const result = primaryResult?.data.slice(0, verticalBars ? 100 : 25);
  const dimensionValues = result.map(d => getRowId(d, dimensionIds));

  const option = useMemo(() => {
    const selectedMetricConfig = metrics.find(m => m.name === selectedMetric);
    const useShareOfTotal = shareOfTotal && metricSupportsShareOfTotal(selectedMetricConfig);
    const dimensionAxis = {
      type: 'category',
      data: dimensionValues,
      axisLabel: {
        formatter: (_, index) => {
          const dimensionValue = result[index];
          return dimensions.map(d => getDimensionValue({ $t, value: dimensionValue[d.name], dimension: d, goalNames, goalNamesLoading })).join(' - ');
        },
        width: 300,
        fontSize: 14,
        overflow: 'breakAll',
      },
      inverse: !verticalBars,
    };
    const metricAxis = {
      type: 'value',
    };

    return {
      grid: {
        left: 20,
        right: verticalBars ? 20 : 40,
        top: verticalBars ? 50 : 40,
        bottom: 20,
        containLabel: true,
      },
      tooltip: {
        trigger: 'item',
        formatter: params => metricsTooltipFormatter({
          $t,
          segments,
          metrics,
          results,
          dimensions,
          rowId: params.name,
          goalNames,
          goalNamesLoading,
          currency: displayCurrency,
        }),
      },
      legend: {
        top: 0,
        padding: [10, 20],
        formatter: (name) => {
          const segment = segments.find(s => s.id === name);
          if (segment) {
            return `${segment.title} - ${formatDateRange(dayjs, segment.dateRange.period, segment.dateRange.startDate, segment.dateRange.endDate, { week: 'Week' })}`;
          }
          return name;
        },
        type: 'scroll',
      },
      yAxis: verticalBars ? metricAxis : dimensionAxis,
      xAxis: verticalBars ? dimensionAxis : metricAxis,
      series: segments.map(s => {
        const { data, totals } = results?.find(r => r.segmentId === s.id) || {};
        return ({
          type: 'bar',
          name: s.id,
          color: s.color,
          barMaxWidth: 50,
          data: result.map(d => {
            const value = data.find(r => getRowId(r, dimensionIds) === getRowId(d, dimensionIds))?.[selectedMetric];
            if (useShareOfTotal) {
              return fraction(value, totals[selectedMetric]);
            }
            return value;
          }),
          label: hideLabels ? undefined : {
            show: true,
            position: verticalBars ? 'top' : 'right',
            fontSize: 14,
            formatter: (params) => {
              const type = useShareOfTotal ? 'percent' : selectedMetricConfig?.type || 'number';
              return getFormatter(type, {
                currency: displayCurrency,
                maximumFractionDigits: getMetricPrecision(selectedMetricConfig, 'display'),
              })(params.value);
            },
          },
          labelLayout: {
            hideOverlap: true,
          },
        });
      }),
    };
  }, [results, metrics, segments, selectedMetric, dimensionIds, goalNames, goalNamesLoading, displayCurrency, $t, verticalBars, hideLabels, shareOfTotal, dimensionValues, result]);

  const height = verticalBars ? 500 : ((20 /* bar height */ * option.series.length) + 10 /* value gap */) * result.length + 50; /* top and bottom margin */

  return (
    <Chart
      option={option}
      height={height}
    />
  );
};

export default BarChartResult;
